import { MatterRowContextMenu } from "@/components/features/matters/MatterRowContextMenu";
import {
  REPORT_CLASSIFICATION_COLUMN_ALIASES,
  REPORT_CLASSIFICATION_COLUMN_ANCHORS,
} from "@/lib/utils/reports/report-classification-columns";
import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { ClaimTypeFilter } from "@/components/features/reports/ClaimTypeFilter";
import { getMatterActivityReportColumns } from "@/components/features/reports/MatterActivityReportCard/matter-activity-report-columns";
import { ReportFilterBar } from "@/components/features/reports/ReportFilterBar";
import { ReportPresetControls } from "@/components/features/reports/ReportPresetControls";
import { ReportsPageLayout } from "@/components/features/reports/ReportsPageLayout";
import {
  Card,
  CardContent,
  DataTable,
  DateFormField,
  Form,
  GlobalNotAuthorized,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { MatterStatusSelectItems } from "@/components/ui/FormFields/MatterStatusSelectItems";
import { useUser } from "@/components/UserProvider";
import { type ReportsMatterActivityDateRangeType } from "@/lib/data/requests";
import { useReportFilterPresets } from "@/hooks/use-report-filter-presets";
import {
  retrieveMatterSettingsQueryOptions,
  retrieveReportsMatterActivityQueryOptions,
} from "@/lib/data/react-query";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { REPORT_FILTER_PRESET_TYPES } from "@/lib/data/static/report-filter-preset-types";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { type ReportsMatterActivity } from "@/lib/types";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDateTime } from "@/lib/utils/format";
import {
  normalizePresetStringArray,
  toReportPresetPreviewFilter,
} from "@/lib/utils/report-filter-presets";
import {
  REPORT_DATE_RANGE_OPTIONS,
  REPORT_DATE_RANGE_SEARCH_VALUES,
  type ReportDateRangeType,
  buildMatterStatusLabelOptions,
  formatResolvedReportDateRangeLabel,
  isReportDateRangeType,
  resolveReportDateRange,
} from "@/lib/utils/reports/report-filter-options";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Activity,
  Clock3,
  FolderOpen,
  ListChecks,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export const Route = createFileRoute("/_auth/reports/matter-activity")({
  component: RouteComponent,
  validateSearch: z.object({
    dateRangeType: z.enum(REPORT_DATE_RANGE_SEARCH_VALUES).optional(),
    dateRangeEnd: z.string().optional(),
    dateRangeStart: z.string().optional(),
    matterStatus: z.string().optional(),
    team: z.string().optional(),
    type: z.string().optional(),
    claimType: z.string().optional(),
  }),
});

const MATTER_ACTIVITY_REPORT_TABLE_NAME =
  tanstackTableNames.reportsMatterActivity;
const MATTER_ACTIVITY_REPORT_PRESET_COLUMN_IDS = {
  actionsCompleted: "Actions Completed",
  activityDate: "Activity Date",
  activityHours: "Activity Hours",
  matter: "Matter",
  matterStatus: "Matter Status",
  matterType: "Incident Type",
  claimTypes: "Claim Types",
  sessions: "Sessions",
  statusCategory: "Status Category",
  team: "Team",
} as const;
const MATTER_ACTIVITY_REPORT_PRESET_COLUMN_KEYS = Object.fromEntries(
  Object.entries(MATTER_ACTIVITY_REPORT_PRESET_COLUMN_IDS).map(
    ([key, value]) => [value, key],
  ),
) as Record<string, keyof typeof MATTER_ACTIVITY_REPORT_PRESET_COLUMN_IDS>;

type MatterActivityPresetFilters = {
  dateRangeEnd?: string;
  dateRangeStart?: string;
  dateRangeType?: string;
  matterStatus?: string[];
  team?: string[];
  type?: string[];
};

const hoursFormatter = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const parseSearchValues = (value?: string) =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const serializeSearchValues = (values: string[]) =>
  values.length ? values.join(",") : undefined;

const addUniqueValue = (values: string[], value: string) =>
  values.includes(value) ? values : [...values, value];

const buildSearchFromPreset = (
  rawFilters: Record<string, unknown>,
): Record<string, string | undefined> => {
  const filters = rawFilters as {
    claimTypeIds?: string[];
    matterTypeIds?: string[];
  } & MatterActivityPresetFilters;
  const dateRangeType = isReportDateRangeType(filters.dateRangeType)
    ? filters.dateRangeType
    : "ALL_TIME";

  return {
    claimType: serializeSearchValues(
      normalizePresetStringArray(filters.claimTypeIds),
    ),
    dateRangeEnd:
      dateRangeType === "CUSTOM_DATE_RANGE" &&
      typeof filters.dateRangeEnd === "string"
        ? filters.dateRangeEnd
        : undefined,
    dateRangeStart:
      dateRangeType === "CUSTOM_DATE_RANGE" &&
      typeof filters.dateRangeStart === "string"
        ? filters.dateRangeStart
        : undefined,
    dateRangeType,
    matterStatus: serializeSearchValues(
      normalizePresetStringArray(filters.matterStatus),
    ),
    team: serializeSearchValues(normalizePresetStringArray(filters.team)),
    type: serializeSearchValues(normalizePresetStringArray(filters.type)),
  };
};

const buildFilterOptions = (
  values: Array<string | null | undefined>,
  selectedValues: string[],
) => {
  const labels = new Set<string>();

  values.forEach((value) => {
    const nextValue = value?.trim();
    if (nextValue) {
      labels.add(nextValue);
    }
  });

  selectedValues.forEach((value) => {
    const nextValue = value.trim();
    if (nextValue) {
      labels.add(nextValue);
    }
  });

  return Array.from(labels)
    .sort((left, right) => left.localeCompare(right))
    .map((value) => ({
      value,
      label: value,
    }));
};

const mapDateRangeTypeToMatterActivity = (
  value: ReportDateRangeType,
): ReportsMatterActivityDateRangeType => value;

const getTotalActionCount = (actions?: Array<Record<string, number>>) =>
  actions?.reduce((sum, item) => {
    return (
      sum +
      Object.values(item).reduce(
        (itemSum, count) =>
          typeof count === "number" && Number.isFinite(count)
            ? itemSum + count
            : itemSum,
        0,
      )
    );
  }, 0) ?? 0;

const formatMatterActivityCsvTimestamp = (value: unknown) => {
  const localValue = convertToLocalFromDatabaseDateTime(
    typeof value === "string" ? value : undefined,
  );
  if (!localValue) return "-";
  return formatDateTime(localValue, false) ?? "-";
};

const formatMatterActivityCsvUser = (value: unknown) => {
  if (typeof value !== "string") {
    return "-";
  }

  const normalizedValue = value.trim();
  return normalizedValue || "-";
};

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { hasPagePermission } = useUser();
  const search = Route.useSearch();

  const selectedDateRange: ReportDateRangeType =
    search.dateRangeType ?? "ALL_TIME";
  const selectedTeams = useMemo(
    () => parseSearchValues(search.team),
    [search.team],
  );
  const selectedClaimTypeIds = useMemo(
    () => parseSearchValues(search.claimType),
    [search.claimType],
  );
  const selectedTypes = useMemo(
    () => parseSearchValues(search.type),
    [search.type],
  );
  const selectedMatterStatuses = useMemo(
    () => parseSearchValues(search.matterStatus),
    [search.matterStatus],
  );
  const dateRangeStart = search.dateRangeStart;
  const dateRangeEnd = search.dateRangeEnd;
  const dateRangeForm = useForm<{
    dateRangeStart: string;
    dateRangeEnd: string;
  }>({
    defaultValues: {
      dateRangeStart: dateRangeStart ?? "",
      dateRangeEnd: dateRangeEnd ?? "",
    },
  });
  const watchedDateRangeStart = dateRangeForm.watch("dateRangeStart");
  const watchedDateRangeEnd = dateRangeForm.watch("dateRangeEnd");
  const [teamSelectValue, setTeamSelectValue] = useState<string | undefined>();
  const [typeSelectValue, setTypeSelectValue] = useState<string | undefined>();
  const [matterStatusSelectValue, setMatterStatusSelectValue] = useState<
    string | undefined
  >();

  useEffect(() => {
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return;
    }
    if (dateRangeStart || dateRangeEnd) {
      return;
    }
    dateRangeForm.reset(
      { dateRangeStart: "", dateRangeEnd: "" },
      { keepDefaultValues: true },
    );
  }, [dateRangeEnd, dateRangeForm, dateRangeStart, selectedDateRange]);

  useEffect(() => {
    dateRangeForm.setValue("dateRangeStart", dateRangeStart ?? "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    dateRangeForm.setValue("dateRangeEnd", dateRangeEnd ?? "", {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [dateRangeEnd, dateRangeForm, dateRangeStart]);

  useEffect(() => {
    if (selectedDateRange !== "CUSTOM_DATE_RANGE") {
      return;
    }
    const nextStart = watchedDateRangeStart.trim();
    const nextEnd = watchedDateRangeEnd.trim();
    if (!nextStart || !nextEnd) {
      return;
    }
    if (nextStart === dateRangeStart && nextEnd === dateRangeEnd) {
      return;
    }
    void navigate({
      search: (prev) => ({
        ...prev,
        dateRangeStart: nextStart,
        dateRangeEnd: nextEnd,
      }),
      replace: true,
    });
  }, [
    dateRangeEnd,
    dateRangeStart,
    navigate,
    selectedDateRange,
    watchedDateRangeEnd,
    watchedDateRangeStart,
  ]);

  const resolvedDateRange = useMemo(
    () =>
      resolveReportDateRange({
        dateRangeEnd,
        dateRangeStart,
        dateRangeType: selectedDateRange,
      }),
    [dateRangeEnd, dateRangeStart, selectedDateRange],
  );
  const effectiveDateRangeType = useMemo<ReportDateRangeType>(
    () =>
      selectedDateRange === "CUSTOM_DATE_RANGE" &&
      (!resolvedDateRange.start || !resolvedDateRange.end)
        ? "ALL_TIME"
        : selectedDateRange,
    [resolvedDateRange.end, resolvedDateRange.start, selectedDateRange],
  );
  const hasActiveDateRange = Boolean(
    effectiveDateRangeType !== "ALL_TIME" &&
      (effectiveDateRangeType !== "CUSTOM_DATE_RANGE" ||
        (resolvedDateRange.start && resolvedDateRange.end)),
  );
  const hasActiveFilters =
    hasActiveDateRange ||
    selectedTeams.length > 0 ||
    selectedTypes.length > 0 ||
    selectedMatterStatuses.length > 0;

  const canViewMatterActivityReport = hasPagePermission(
    PAGE_PERMISSION_KEYS.mattersReportsActivity,
  );

  const { data: matterSettingsData } = useQuery({
    ...retrieveMatterSettingsQueryOptions(),
    enabled: canViewMatterActivityReport,
  });

  const teamOptions = useMemo(
    () =>
      buildFilterOptions(
        (matterSettingsData?.data.teams ?? []).map((team) => team.label),
        selectedTeams,
      ),
    [matterSettingsData?.data.teams, selectedTeams],
  );
  const typeOptions = useMemo(
    () =>
      buildFilterOptions(
        (matterSettingsData?.data.incidentTypes ?? []).map((incidentType) =>
          getIncidentTypeLabel(incidentType),
        ),
        selectedTypes,
      ),
    [matterSettingsData?.data.incidentTypes, selectedTypes],
  );
  const matterStatusOptions = useMemo(
    () =>
      buildMatterStatusLabelOptions(
        matterSettingsData?.data.statuses,
        selectedMatterStatuses,
      ),
    [matterSettingsData?.data.statuses, selectedMatterStatuses],
  );

  const resolveOptionLabel = (
    options: ReadonlyArray<{ value: string; label: string }>,
    value: string,
  ) => options.find((option) => option.value === value)?.label ?? value;

  const reportFilters = useMemo(() => {
    const teamIds = selectedTeams.map(
      (label) =>
        matterSettingsData?.data.teams.find((team) => team.label === label)
          ?.id ?? label,
    );
    const incidentTypeIds = selectedTypes.map(
      (label) =>
        (matterSettingsData?.data.incidentTypes ?? []).find(
          (type) => getIncidentTypeLabel(type) === label,
        )?.id ?? label,
    );
    const matterStatusIds = selectedMatterStatuses.map(
      (label) =>
        matterSettingsData?.data.statuses.find(
          (status) => status.label === label,
        )?.id ?? label,
    );

    return {
      dateRange: {
        preset: mapDateRangeTypeToMatterActivity(effectiveDateRangeType),
        ...(effectiveDateRangeType === "CUSTOM_DATE_RANGE" &&
        resolvedDateRange.start &&
        resolvedDateRange.end
          ? {
              endDate: resolvedDateRange.end,
              startDate: resolvedDateRange.start,
            }
          : {}),
      },
      filters: {
        matterStatusIds,
        incidentTypeIds,
        teamIds,
        claimTypeIds: selectedClaimTypeIds,
      },
    };
  }, [
    effectiveDateRangeType,
    resolvedDateRange.end,
    resolvedDateRange.start,
    selectedMatterStatuses,
    selectedTeams,
    selectedTypes,
    selectedClaimTypeIds,
    matterSettingsData?.data.statuses,
    matterSettingsData?.data.teams,
    matterSettingsData?.data.incidentTypes,
  ]);

  const { data, isLoading, error } = useQuery({
    ...retrieveReportsMatterActivityQueryOptions(reportFilters),
    enabled: canViewMatterActivityReport,
  });

  const reportColumns = useMemo(() => getMatterActivityReportColumns(), []);
  const matterActivityPresetColumnKeysById = useMemo(
    () =>
      reportColumns.reduce<Record<string, string>>((acc, column) => {
        if (typeof column.id !== "string") return acc;
        if (!(column.id in MATTER_ACTIVITY_REPORT_PRESET_COLUMN_KEYS)) {
          return acc;
        }
        acc[column.id] = MATTER_ACTIVITY_REPORT_PRESET_COLUMN_KEYS[column.id];
        return acc;
      }, {}),
    [reportColumns],
  );

  const reports = useMemo(() => data?.data ?? [], [data?.data]);
  const totalEntries = reports.length;
  const uniqueMatters = useMemo(
    () =>
      new Set(
        reports
          .map((row) => row.matterActivityReportAggregateMatterId)
          .filter((value): value is string => Boolean(value)),
      ).size,
    [reports],
  );
  const totalHours = data?.metrics.totalActivityHours ?? 0;
  const totalActions = useMemo(
    () =>
      reports.reduce(
        (sum, row) =>
          sum +
          getTotalActionCount(
            row.matterActivityReportAggregateActionsCompleted,
          ),
        0,
      ),
    [reports],
  );

  const claimFilterChips = selectedClaimTypeIds.map((id) => ({
    key: `claimType:${id}`,
    label: `Claim type: ${(matterSettingsData?.data.claimTypes ?? []).find((claimType) => claimType.id === id)?.label ?? id}`,
    onRemove: () => {
      void navigate({
        search: (prev) => ({
          ...prev,
          claimType: serializeSearchValues(
            selectedClaimTypeIds.filter((value) => value !== id),
          ),
        }),
        replace: true,
      });
    },
  }));
  const appliedFilterChips = useMemo(() => {
    const chips: Array<{
      key: string;
      label: string;
      onRemove: () => void;
    }> = [];

    if (hasActiveDateRange) {
      const label =
        formatResolvedReportDateRangeLabel({
          dateRangeEnd: resolvedDateRange.end,
          dateRangeStart: resolvedDateRange.start,
          dateRangeType: selectedDateRange,
        }) ?? selectedDateRange;
      chips.push({
        key: `dateRange:${resolvedDateRange.start ?? ""}-${resolvedDateRange.end ?? ""}`,
        label: `Date range: ${label}`,
        onRemove: () => {
          void navigate({
            search: (prev) => ({
              ...prev,
              dateRangeType: "ALL_TIME",
              dateRangeEnd: undefined,
              dateRangeStart: undefined,
            }),
            replace: true,
          });
        },
      });
    }

    selectedTeams.forEach((value) => {
      chips.push({
        key: `team:${value}`,
        label: `Team: ${resolveOptionLabel(teamOptions, value)}`,
        onRemove: () => {
          const next = selectedTeams.filter((item) => item !== value);
          void navigate({
            search: (prev) => ({
              ...prev,
              team: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedTypes.forEach((value) => {
      chips.push({
        key: `type:${value}`,
        label: `Type: ${resolveOptionLabel(typeOptions, value)}`,
        onRemove: () => {
          const next = selectedTypes.filter((item) => item !== value);
          void navigate({
            search: (prev) => ({
              ...prev,
              type: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    selectedMatterStatuses.forEach((value) => {
      chips.push({
        key: `matterStatus:${value}`,
        label: `Matter Status: ${resolveOptionLabel(matterStatusOptions, value)}`,
        onRemove: () => {
          const next = selectedMatterStatuses.filter((item) => item !== value);
          void navigate({
            search: (prev) => ({
              ...prev,
              matterStatus: serializeSearchValues(next),
            }),
            replace: true,
          });
        },
      });
    });

    return chips;
  }, [
    hasActiveDateRange,
    matterStatusOptions,
    navigate,
    resolvedDateRange.end,
    resolvedDateRange.start,
    selectedDateRange,
    selectedMatterStatuses,
    selectedTeams,
    selectedTypes,
    teamOptions,
    typeOptions,
  ]);

  const {
    applyPresetByValue,
    clearSelectedPreset,
    createReportFilterPresetMutation,
    deletePresetByValue,
    deleteReportFilterPresetMutation,
    handleColumnOrderChange,
    handleColumnVisibilityChange,
    isReportFilterPresetsLoading,
    isSavePresetOpen,
    presetDescription,
    presetName,
    presetOptions,
    presetSelectValue,
    savePreset,
    savePreviewColumns,
    setIsSavePresetOpen,
    setPresetDescription,
    setPresetName,
    tableRenderKey,
  } = useReportFilterPresets({
    availableColumnIds: reportColumns
      .map((column) => column.id)
      .filter((columnId): columnId is string => typeof columnId === "string"),
    buildSearchFromPreset,
    currentFilterOptions: {
      claimTypeIds: selectedClaimTypeIds,
      dateRangeEnd:
        selectedDateRange === "CUSTOM_DATE_RANGE"
          ? resolvedDateRange.end
          : undefined,
      dateRangeStart:
        selectedDateRange === "CUSTOM_DATE_RANGE"
          ? resolvedDateRange.start
          : undefined,
      dateRangeType: selectedDateRange,
      matterStatus: selectedMatterStatuses,
      team: selectedTeams,
      type: selectedTypes,
    },
    enabled: canViewMatterActivityReport,
    lockedVisibleColumnIds: [MATTER_ACTIVITY_REPORT_PRESET_COLUMN_IDS.matter],
    onApplySearch: async (nextSearch) => {
      await navigate({
        search: () => nextSearch,
        replace: true,
      });
    },
    presetKeyByColumnId: matterActivityPresetColumnKeysById,
    reportType: REPORT_FILTER_PRESET_TYPES.matterActivity,
    tableName: MATTER_ACTIVITY_REPORT_TABLE_NAME,
  });
  const getMatterActivityCsvValue = (
    row: ReportsMatterActivity,
    columnId: string,
  ) => {
    switch (columnId) {
      case "Claim Types":
        return (
          (row.claimTypes ?? [])
            .map((claimType) => claimType.label)
            .join(", ") || "-"
        );
      case "Incident Type":
        return row.matterActivityReportAggregateIncidentType ?? "-";
      case "Matter":
        return row.matterTitle;
      case "Last Viewed":
        return formatMatterActivityCsvTimestamp(row.matterLastSeenDatetime);
      case "Last Viewed By":
        return formatMatterActivityCsvUser(row.matterLastSeenUsername);
      case "Last Activity":
        return formatMatterActivityCsvTimestamp(row.matterLastTouchDatetime);
      case "Last Activity By":
        return formatMatterActivityCsvUser(row.matterLastTouchUsername);
      case "Last Activity Message":
        return row.matterLastTouchMessage || "-";
      default:
        return "";
    }
  };

  const summaryCards: Array<{
    icon: LucideIcon;
    label: string;
    value: string;
  }> = [
    {
      icon: Activity,
      label: "Activity entries",
      value: totalEntries.toLocaleString(),
    },
    {
      icon: FolderOpen,
      label: "Unique matters",
      value: uniqueMatters.toLocaleString(),
    },
    {
      icon: Clock3,
      label: "Hours logged",
      value: hoursFormatter.format(totalHours),
    },
    {
      icon: ListChecks,
      label: "Actions completed",
      value: totalActions.toLocaleString(),
    },
  ];

  if (!canViewMatterActivityReport) {
    return <GlobalNotAuthorized />;
  }

  return (
    <ReportsPageLayout
      title="Matter activity report"
      headerActions={
        <ReportPresetControls
          isDeleting={deleteReportFilterPresetMutation.isPending}
          isPresetListLoading={isReportFilterPresetsLoading}
          isSaveOpen={isSavePresetOpen}
          isSaving={createReportFilterPresetMutation.isPending}
          onDeletePresetValue={(value) => {
            void deletePresetByValue(value);
          }}
          onPresetDescriptionChange={setPresetDescription}
          onPresetNameChange={setPresetName}
          onPresetValueChange={(value) => {
            void applyPresetByValue(value);
          }}
          onSave={() => {
            void savePreset();
          }}
          onSaveOpenChange={setIsSavePresetOpen}
          presetDescription={presetDescription}
          presetName={presetName}
          presetOptions={presetOptions}
          presetValue={presetSelectValue}
          savePreviewColumns={savePreviewColumns}
          savePreviewFilters={[...appliedFilterChips, ...claimFilterChips].map(
            (chip) => toReportPresetPreviewFilter(chip.label),
          )}
          saveTitle="Save matter activity report preset"
          selectId="matter-activity-report-preset-select"
        />
      }
    >
      <ReportFilterBar
        appliedFilters={[...appliedFilterChips, ...claimFilterChips].map(
          (chip) => ({
            ...chip,
            onRemove: () => {
              clearSelectedPreset();
              chip.onRemove();
            },
          }),
        )}
        onClearFilters={async () => {
          clearSelectedPreset();
          dateRangeForm.reset(
            { dateRangeStart: "", dateRangeEnd: "" },
            { keepDefaultValues: true },
          );
          setTeamSelectValue(undefined);
          setTypeSelectValue(undefined);
          setMatterStatusSelectValue(undefined);
          await navigate({
            search: () => ({
              dateRangeType: "ALL_TIME",
              dateRangeEnd: undefined,
              dateRangeStart: undefined,
              matterStatus: undefined,
              team: undefined,
              type: undefined,
              claimType: undefined,
            }),
            replace: true,
          });
        }}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            value={selectedDateRange}
            onValueChange={async (value) => {
              const nextValue = value as ReportDateRangeType;
              await navigate({
                search: (prev) => ({
                  ...prev,
                  dateRangeType: nextValue,
                  dateRangeEnd:
                    nextValue === "CUSTOM_DATE_RANGE"
                      ? prev.dateRangeEnd
                      : undefined,
                  dateRangeStart:
                    nextValue === "CUSTOM_DATE_RANGE"
                      ? prev.dateRangeStart
                      : undefined,
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select date range" />
            </SelectTrigger>
            <SelectContent>
              {REPORT_DATE_RANGE_OPTIONS.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={teamSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedTeams, value);
              setTeamSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  team: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select teams" />
            </SelectTrigger>
            <SelectContent>
              {teamOptions.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <ClaimTypeFilter
            value={selectedClaimTypeIds}
            onChange={(value) => {
              void navigate({
                search: (prev) => ({
                  ...prev,
                  claimType: serializeSearchValues(value),
                }),
                replace: true,
              });
            }}
          />
          <Select
            value={typeSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedTypes, value);
              setTypeSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  type: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select types" />
            </SelectTrigger>
            <SelectContent>
              {typeOptions.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={matterStatusSelectValue ?? ""}
            onValueChange={async (value) => {
              const next = addUniqueValue(selectedMatterStatuses, value);
              setMatterStatusSelectValue(undefined);
              await navigate({
                search: (prev) => ({
                  ...prev,
                  matterStatus: serializeSearchValues(next),
                }),
                replace: true,
              });
            }}
          >
            <SelectTrigger className="h-11">
              <SelectValue placeholder="Select statuses" />
            </SelectTrigger>
            <SelectContent>
              <MatterStatusSelectItems items={matterStatusOptions} />
            </SelectContent>
          </Select>
          {selectedDateRange === "CUSTOM_DATE_RANGE" && (
            <Form {...dateRangeForm}>
              <div className="grid grid-cols-2 gap-3 sm:col-span-2 lg:col-span-2">
                <DateFormField
                  name="dateRangeStart"
                  label="Start date"
                  clearable={false}
                  register={dateRangeForm.register("dateRangeStart")}
                />
                <DateFormField
                  name="dateRangeEnd"
                  label="End date"
                  clearable={false}
                  register={dateRangeForm.register("dateRangeEnd")}
                />
              </div>
            </Form>
          )}
        </div>
      </ReportFilterBar>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {summaryCards.map((card) => (
          <Card className="border-border/60 shadow-sm" key={card.label}>
            <CardContent className="space-y-2 pt-4">
              {isLoading ? (
                <>
                  <div className="flex items-center gap-3">
                    <div className="bg-muted h-10 w-10 animate-pulse rounded-full" />
                    <div className="bg-muted h-4 w-32 animate-pulse rounded" />
                  </div>
                  <div className="bg-muted h-7 w-20 animate-pulse rounded" />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <div className="bg-muted text-foreground flex h-10 w-10 items-center justify-center rounded-full">
                      <card.icon className="h-5 w-5" />
                    </div>
                    <Text variant="subHeading">{card.label}</Text>
                  </div>
                  <Text variant="title" className="text-2xl leading-8">
                    {card.value}
                  </Text>
                </>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <DataTable
        entityLabelPlural="matters"
        columnIdAliases={REPORT_CLASSIFICATION_COLUMN_ALIASES}
        newColumnAnchors={REPORT_CLASSIFICATION_COLUMN_ANCHORS}
        key={tableRenderKey}
        hideSearchBar
        columns={reportColumns}
        data={reports}
        onColumnOrderChange={handleColumnOrderChange}
        onColumnVisibilityChange={handleColumnVisibilityChange}
        renderRowContextMenu={(row, trigger) => {
          const matterId = row.original.matterActivityReportAggregateMatterId;
          return matterId ? (
            <MatterRowContextMenu matterId={matterId} trigger={trigger} />
          ) : (
            trigger
          );
        }}
        onRowClick={async (row) => {
          const matterId = row.original.matterActivityReportAggregateMatterId;
          if (!matterId) return;

          await navigate({
            to: "/matters/$matterId",
            params: { matterId },
          });
        }}
        tableName={MATTER_ACTIVITY_REPORT_TABLE_NAME}
        isLoading={isLoading}
        isError={Boolean(error)}
        noResultsMessage={
          hasActiveFilters
            ? "No matter activity found for the selected filters."
            : "No matter activity found."
        }
        leftToolbarContent={
          <Text variant="body" className="text-lg font-semibold">
            Matter activity
          </Text>
        }
        exportCsv={{
          fileName: "matter-activity-report.csv",
          getCellValue: getMatterActivityCsvValue,
        }}
      />
    </ReportsPageLayout>
  );
}
